<template>
  <LoadingElement v-if="authStore.isLoading" />
  <div class="main bg-primary" v-else>
    <div class="rowSpaceBetween fullWidth mb-5">
      <TopDeck
        firstElement="Mon"
        secondElement="profil"
        coloredSecond="true"
      ></TopDeck>
      <div class="noSpaceRowAction">
        <ThemeToggle />
        <AccountToggle displayLogout="true" />
      </div>
    </div>
    <div class="myBasicInfosContainer">
      <div class="basicInfosHeader">
        <div class="basicInfosTitle">
          <v-icon size="20">mdi-account-circle</v-icon>
          <h2>Mes informations de base</h2>
        </div>
        <v-btn
          color="accent"
          variant="outlined"
          class="basicInfosButton"
          rounded="xl"
          size="small"
          @click="changePassword"
        >
          <v-icon icon="mdi-pencil" class="mr-2"></v-icon>
          Changer mon mot de passe
        </v-btn>
      </div>
      <div class="basicInfosBody">
        <div class="basicInfosRow">
          <span class="basicInfosLabel">Nom</span>
          <span class="basicInfosValue">{{ authStore.userInfos?.username || "—" }}</span>
        </div>
        <div class="basicInfosRow">
          <span class="basicInfosLabel">Email</span>
          <span class="basicInfosValue">{{ authStore.userInfos?.email || "—" }}</span>
        </div>
      </div>
    </div>
    <HealthStatsBloc />
    <Info_display_bloc :user="authStore.userInfos" />
  </div>

  <v-snackbar
    v-model="showSnack"
    :color="snackColor"
    :timeout="2000"
    location="bottom"
  >
    {{ snackMessage }}
  </v-snackbar>

  <!-- 🔹 Modal de changement de mot de passe -->
  <v-dialog v-model="showPasswordDialog" max-width="420">
    <v-card class="passwordModal">
      <v-card-title class="passwordModal__header">
        <div class="passwordModal__title">
          <v-icon class="passwordModal__icon">mdi-lock-reset</v-icon>
          <span>Changer mon mot de passe</span>
        </div>
        <v-btn icon variant="text" class="passwordModal__close" @click="showPasswordDialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />
      <v-card-text class="passwordModal__body">
        <form @submit.prevent="onSubmit" class="passwordModal__form">
          <v-text-field
            v-model="email"
            type="email"
            label="Votre e-mail"
            variant="outlined"
            density="compact"
            required
            prepend-inner-icon="mdi-email-outline"
          />
          <v-btn
            :loading="loading"
            type="submit"
            color="accent"
            rounded="xl"
            class="passwordModal__submit"
          >
            <v-icon icon="mdi-email-fast-outline" class="mr-2" />
            {{ loading ? "Envoi…" : "Envoyer le lien" }}
          </v-btn>
          <p v-if="done" class="passwordModal__hint">
            Si un compte existe pour cet e-mail, un message vient d’être envoyé.
          </p>
          <p v-if="error" class="passwordModal__error">{{ error }}</p>
        </form>
      </v-card-text>
      <v-card-actions class="passwordModal__actions">
        <v-spacer />
        <v-btn variant="text" class="passwordModal__ghost" @click="showPasswordDialog = false">
          Fermer
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref } from "vue";
import { getAuth, signOut } from "firebase/auth";
import { useRouter } from "vue-router";
import TopDeck from "../Utils/TopDeck.vue";
import LoadingElement from "../Animations/LoadingElement.vue";
import { useAuthStore } from "@/store/auth_store";
import Info_display_bloc from "./InfoDisplayBlock.vue";
import { requestPasswordReset } from "@/service/auth_service";
import HealthStatsBloc from "./HealthStatsBloc.vue";
import ThemeToggle from "@/components/Utils/ThemeToggle.vue";
import AccountToggle from "@/components/Utils/AccountToggle.vue";

export default {
  name: "ProfilPage",
  components: {
    LoadingElement,
    TopDeck,
    Info_display_bloc,
    HealthStatsBloc,
    ThemeToggle,
    AccountToggle,
  },
  setup() {
    const router = useRouter();
    const auth = getAuth();
    const authStore = useAuthStore();
    // Thème géré par ThemeToggle
    const showSnack = ref(false);
    const snackMessage = ref("");
    const snackColor = ref("success");

    const showPasswordDialog = ref(false);
    const email = ref("");
    const loading = ref(false);
    const done = ref(false);
    const error = ref("");

    const changePassword = () => {
      showPasswordDialog.value = true;
    };

    async function onSubmit() {
      loading.value = true;
      error.value = "";
      try {
        await requestPasswordReset(email.value);
        done.value = true; // message générique
      } catch (e) {
        console.error("requestPasswordReset error", e);
        done.value = true;
      } finally {
        loading.value = false;
      }
    }

    const handleSignout = () => {
      signOut(auth)
        .then(() => {
          router.push("/login");
        })
        .catch((error) => {
          console.error(error);
        });
    };

    return {
      authStore,
      showSnack,
      snackMessage,
      snackColor,
      showPasswordDialog,
      email,
      loading,
      done,
      error,
      onSubmit,
      handleSignout,
      changePassword,
    };
  },
};
</script>

<style scoped>
.noSpaceRowAction {
  display: flex;
  justify-content: flex-start;
  align-items: center;
}

.basicInfosHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.basicInfosTitle {
  display: flex;
  align-items: center;
  gap: 4px;
}

.basicInfosTitle h2 {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.myBasicInfosContainer {
  padding: 18px 20px;
  background: linear-gradient(
    145deg,
    rgba(var(--v-theme-secondary), 0.92),
    rgba(var(--v-theme-secondary), 0.7)
  );
  border-radius: 18px;
  border: 1px solid rgba(var(--v-theme-text), 0.08);
  box-shadow: 0 18px 36px rgba(0, 0, 0, 0.12);
}

.basicInfosBody {
  display: grid;
  gap: 10px;
}

.basicInfosRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(var(--v-theme-primary), 0.5);
  border: 1px solid rgba(var(--v-theme-text), 0.06);
}

.basicInfosLabel {
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: rgba(var(--v-theme-text), 0.7);
  text-transform: uppercase;
}

.basicInfosValue {
  font-size: 1rem;
  font-weight: 600;
  color: rgb(var(--v-theme-text));
}

.basicInfosButton {
  box-shadow: none;
  text-transform: none;
  background: rgba(var(--v-theme-primary), 0.35);
  border-color: rgba(var(--v-theme-accent), 0.6);
}

.passwordModal {
  border-radius: 20px;
  border: 1px solid rgba(var(--v-theme-text), 0.08);
  background: rgb(var(--v-theme-surface));
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.18);
}

.passwordModal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px 12px;
  background: rgba(var(--v-theme-primary), 0.06);
}

.passwordModal__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.1rem;
  font-weight: 700;
}

.passwordModal__icon {
  color: rgb(var(--v-theme-accent));
}

.passwordModal__close {
  border-radius: 999px;
}

.passwordModal__body {
  padding: 18px 20px 6px;
  background: rgb(var(--v-theme-surface));
}

.passwordModal__form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.passwordModal__submit {
  text-transform: none;
  font-weight: 700;
  box-shadow: 0 14px 26px rgba(0, 0, 0, 0.2);
}

.passwordModal__hint {
  margin: 0;
  font-size: 0.9rem;
  color: rgba(var(--v-theme-text), 0.7);
}

.passwordModal__error {
  margin: 0;
  font-size: 0.9rem;
  color: rgb(var(--v-theme-error));
  font-weight: 600;
}

.passwordModal__actions {
  padding: 6px 16px 16px;
  background: rgb(var(--v-theme-surface));
}

.passwordModal__ghost {
  text-transform: none;
  font-weight: 600;
  border-radius: 999px;
  padding: 6px 14px;
  background: rgba(var(--v-theme-primary), 0.08);
  border: 1px solid rgba(var(--v-theme-text), 0.18);
}

.passwordModal :deep(.v-field) {
  border-radius: 14px;
  background: rgb(var(--v-theme-surface));
}

.exportSetting {
  display: flex;
  align-items: center;
  justify-content: center;
  align-items: center;
}

@media (min-width: 768px) {
  .absoluteImage {
    width: 20%;
  }
}

@media (min-width: 1024px) {
  .absoluteImage {
    width: 30%;
  }
}

@media (min-width: 1440px) {
  .absoluteImage {
    width: 30%;
  }
}

@media (min-width: 1560px) {
  .absoluteImage {
    width: 25%;
  }
}

@media (min-width: 1900px) {
  .absoluteImage {
    width: 20%;
  }
}

@media (min-width: 2300px) {
  .absoluteImage {
    width: 15%;
  }
}

@media (min-width: 2700px) {
  .absoluteImage {
    width: 12%;
  }
}
</style>
